import jsPDF from "jspdf";

const MARGIN = 14;
const PAGE_WIDTH = 210;
const CONTENT_WIDTH = PAGE_WIDTH - MARGIN * 2;
const LABEL_COL_WIDTH = 52;
const VALUE_COL_WIDTH = CONTENT_WIDTH - LABEL_COL_WIDTH;
const BOTTOM_MARGIN = 16;
const SECTION_GAP = 8;
const ROW_PADDING = 2.5;
const PX = 25.4 / 96;

const QUESTION_FONT_SIZE = 12.5;
const ANSWER_FONT_SIZE = 11;
const ANSWER_LINE_HEIGHT_FACTOR = 1.55;
const QUESTION_LINE_HEIGHT_FACTOR = 1.3;
const QUESTION_MARGIN_TOP = 30 * PX;
const QUESTION_MARGIN_BOTTOM = 1 * PX;
const ANSWER_PADDING = 14 * PX;
const SECTION_SPACER = 24 * PX;
const PARAGRAPH_GAP = 5 * PX;
const BORDER_RADIUS = 5 * PX;
const ANSWER_BORDER_WIDTH = 0.3;

const COLORS = {
  text: [33, 33, 33],
  border: [190, 190, 190],
  answerFill: [249, 249, 249],
  answerBorder: [217, 217, 217],
  divider: [200, 200, 200],
};

const VISIT_DETAIL_LABELS = new Set(["travel time"]);

function setTextColor(doc) {
  doc.setTextColor(...COLORS.text);
}

function getTextLineHeight(doc, fontSize, lineHeightFactor) {
  return (fontSize * lineHeightFactor) / doc.internal.scaleFactor;
}

function ensureSpace(doc, y, neededHeight) {
  const maxY = doc.internal.pageSize.height - BOTTOM_MARGIN;
  if (y + neededHeight >= maxY) {
    doc.addPage();
    return MARGIN;
  }
  return y;
}

function measureWrappedLines(doc, text, maxWidth, fontSize, fontStyle = "normal") {
  doc.setFont("helvetica", fontStyle);
  doc.setFontSize(fontSize);
  return doc.splitTextToSize(`${text ?? ""}`, maxWidth);
}

function getQuestionTextHeight(lineCount, lineHeight) {
  if (lineCount <= 0) return 0;
  return (lineCount - 1) * lineHeight + lineHeight * 0.85;
}

function drawTitle(doc, y, title) {
  doc.setFont("helvetica", "bold");
  doc.setFontSize(21);
  setTextColor(doc);
  doc.text(title, PAGE_WIDTH / 2, y, { align: "center" });
  return y + 10;
}

function drawDivider(doc, y) {
  y = ensureSpace(doc, y, 4);
  doc.setDrawColor(...COLORS.divider);
  doc.setLineWidth(0.3);
  doc.line(MARGIN, y, PAGE_WIDTH - MARGIN, y);
  return y + SECTION_GAP;
}

function drawSectionHeading(doc, y, title) {
  y = ensureSpace(doc, y, 10);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(15);
  setTextColor(doc);
  doc.text(title, MARGIN, y);
  return y + 6;
}

class PdfLayoutContext {
  constructor(doc, startY = MARGIN) {
    this.doc = doc;
    this.y = startY;
  }

  get maxY() {
    return this.doc.internal.pageSize.height - BOTTOM_MARGIN;
  }

  ensureSpace(neededHeight) {
    if (this.y + neededHeight >= this.maxY) {
      this.doc.addPage();
      this.y = MARGIN;
    }
  }

  advance(delta) {
    this.y += delta;
  }

  newPage() {
    this.doc.addPage();
    this.y = MARGIN;
  }
}

class QuestionAnswerSection {
  constructor(doc, question, answer) {
    this.doc = doc;
    this.answerLineHeight = getTextLineHeight(
      doc,
      ANSWER_FONT_SIZE,
      ANSWER_LINE_HEIGHT_FACTOR
    );
    this.questionLines = measureWrappedLines(
      doc,
      question,
      CONTENT_WIDTH,
      QUESTION_FONT_SIZE,
      "bold"
    );
    this.questionLineHeight = getTextLineHeight(
      doc,
      QUESTION_FONT_SIZE,
      QUESTION_LINE_HEIGHT_FACTOR
    );
    this.questionBlockHeight =
      getQuestionTextHeight(this.questionLines.length, this.questionLineHeight) +
      QUESTION_MARGIN_BOTTOM;
    this.minAnswerHeight = ANSWER_PADDING * 2 + this.answerLineHeight;
    this.answerUnits = buildAnswerLayout(
      doc,
      answer,
      CONTENT_WIDTH - ANSWER_PADDING * 2
    );
  }

  render(ctx, { addTopSpacing = false } = {}) {
    const blockHeight = this.questionBlockHeight + this.minAnswerHeight;
    ctx.ensureSpace((addTopSpacing ? QUESTION_MARGIN_TOP : 0) + blockHeight);

    if (addTopSpacing) {
      ctx.advance(QUESTION_MARGIN_TOP);
    }

    this.#renderQuestion(ctx);
    this.#renderAnswer(ctx);
    this.#renderSectionSpacer(ctx);
  }

  #renderQuestion(ctx) {
    this.doc.setFont("helvetica", "bold");
    this.doc.setFontSize(QUESTION_FONT_SIZE);
    setTextColor(this.doc);
    this.doc.text(this.questionLines, MARGIN, ctx.y, {
      lineHeightFactor: QUESTION_LINE_HEIGHT_FACTOR,
    });

    ctx.advance(
      getQuestionTextHeight(this.questionLines.length, this.questionLineHeight) +
        QUESTION_MARGIN_BOTTOM
    );
  }

  #renderAnswer(ctx) {
    let pendingUnits = [...this.answerUnits];
    let fragmentIndex = 0;

    while (pendingUnits.length) {
      let availableContentHeight = ctx.maxY - ctx.y - ANSWER_PADDING * 2;

      if (availableContentHeight < this.answerLineHeight) {
        ctx.newPage();
        availableContentHeight = ctx.maxY - ctx.y - ANSWER_PADDING * 2;
      }

      const { chunkUnits, remainingUnits } = takeAnswerChunk(
        pendingUnits,
        availableContentHeight,
        this.answerLineHeight
      );
      pendingUnits = remainingUnits;

      const boxContentHeight = chunkUnits.reduce(
        (sum, unit) => measureUnitHeight(unit, this.answerLineHeight) + sum,
        0
      );
      const boxHeight = ANSWER_PADDING * 2 + boxContentHeight;

      drawAnswerFragmentBox(
        this.doc,
        MARGIN,
        ctx.y,
        CONTENT_WIDTH,
        boxHeight,
        resolveFragmentRole(fragmentIndex, pendingUnits.length)
      );
      drawAnswerUnits(
        this.doc,
        MARGIN + ANSWER_PADDING,
        ctx.y,
        chunkUnits,
        this.answerLineHeight
      );

      ctx.advance(boxHeight);
      fragmentIndex += 1;
    }
  }

  #renderSectionSpacer(ctx) {
    if (ctx.y + SECTION_SPACER >= ctx.maxY) {
      ctx.newPage();
    }
    ctx.advance(SECTION_SPACER);
  }
}

function resolveFragmentRole(fragmentIndex, remainingUnitCount) {
  const isFirst = fragmentIndex === 0;
  const isLast = remainingUnitCount === 0;

  if (isFirst && isLast) return "only";
  if (isFirst) return "first";
  if (isLast) return "last";
  return "middle";
}

function drawAnswerFragmentBox(doc, x, y, width, height, role) {
  const hasTop = role === "first" || role === "only";
  const hasBottom = role === "last" || role === "only";

  if (role === "only" && typeof doc.roundedRect === "function") {
    doc.setFillColor(...COLORS.answerFill);
    doc.setDrawColor(...COLORS.answerBorder);
    doc.setLineWidth(ANSWER_BORDER_WIDTH);
    doc.roundedRect(x, y, width, height, BORDER_RADIUS, BORDER_RADIUS, "FD");
    return;
  }

  doc.setFillColor(...COLORS.answerFill);
  doc.rect(x, y, width, height, "F");
  doc.setDrawColor(...COLORS.answerBorder);
  doc.setLineWidth(ANSWER_BORDER_WIDTH);
  doc.line(x, y, x, y + height);
  doc.line(x + width, y, x + width, y + height);

  if (hasTop) doc.line(x, y, x + width, y);
  if (hasBottom) doc.line(x, y + height, x + width, y + height);
}

function drawTableSection(doc, y, title, rows) {
  if (!rows?.length) return y;

  y = ensureSpace(doc, y, 18);
  y = drawSectionHeading(doc, y, title);

  rows.forEach((row, index) => {
    const lineHeight = getTextLineHeight(doc, ANSWER_FONT_SIZE, ANSWER_LINE_HEIGHT_FACTOR);
    const labelLines = measureWrappedLines(
      doc,
      row.label,
      LABEL_COL_WIDTH - ANSWER_PADDING * 2,
      ANSWER_FONT_SIZE,
      "bold"
    );
    const valueLines = measureWrappedLines(
      doc,
      row.value ?? "--",
      VALUE_COL_WIDTH - ANSWER_PADDING * 2,
      ANSWER_FONT_SIZE,
      "normal"
    );
    const rowHeight =
      Math.max(labelLines.length, valueLines.length) * lineHeight + ROW_PADDING * 2;

    y = ensureSpace(doc, y, rowHeight + 2);
    if (y === MARGIN && index > 0) {
      y = drawSectionHeading(doc, y, title);
    }

    const rowTopY = y;

    doc.setDrawColor(...COLORS.border);
    doc.setLineWidth(0.3);
    doc.rect(MARGIN, rowTopY, CONTENT_WIDTH, rowHeight);
    doc.line(MARGIN + LABEL_COL_WIDTH, rowTopY, MARGIN + LABEL_COL_WIDTH, rowTopY + rowHeight);

    doc.setFont("helvetica", "bold");
    doc.setFontSize(ANSWER_FONT_SIZE);
    setTextColor(doc);
    doc.text(
      labelLines,
      MARGIN + ANSWER_PADDING,
      rowTopY + ROW_PADDING + lineHeight * 0.85,
      { lineHeightFactor: ANSWER_LINE_HEIGHT_FACTOR }
    );

    doc.setFont("helvetica", "normal");
    doc.text(
      valueLines,
      MARGIN + LABEL_COL_WIDTH + ANSWER_PADDING,
      rowTopY + ROW_PADDING + lineHeight * 0.85,
      { lineHeightFactor: ANSWER_LINE_HEIGHT_FACTOR }
    );

    y += rowHeight;
  });

  return y + SECTION_GAP;
}

function buildAnswerLayout(doc, text, maxWidth) {
  const raw = `${text ?? ""}`;
  if (!raw.trim()) return [{ type: "line", text: "" }];

  const paragraphs = raw.split(/\r?\n/);
  const units = [];

  paragraphs.forEach((paragraph, index) => {
    if (index > 0) units.push({ type: "gap" });

    const trimmed = paragraph.trim();
    if (!trimmed) {
      if (index > 0 || paragraphs.length > 1) units.push({ type: "gap" });
      return;
    }

    measureWrappedLines(doc, trimmed, maxWidth, ANSWER_FONT_SIZE, "normal").forEach(
      (line) => units.push({ type: "line", text: line })
    );
  });

  return units.length ? units : [{ type: "line", text: "" }];
}

function measureUnitHeight(unit, answerLineHeight) {
  return unit.type === "gap" ? PARAGRAPH_GAP : answerLineHeight;
}

function takeAnswerChunk(units, maxContentHeight, answerLineHeight) {
  const chunkUnits = [];
  let chunkContentHeight = 0;
  let index = 0;

  while (index < units.length) {
    const unitHeight = measureUnitHeight(units[index], answerLineHeight);

    if (chunkUnits.length > 0 && chunkContentHeight + unitHeight > maxContentHeight) {
      break;
    }

    chunkUnits.push(units[index]);
    chunkContentHeight += unitHeight;
    index += 1;

    if (chunkContentHeight >= maxContentHeight) break;
  }

  return { chunkUnits, remainingUnits: units.slice(index) };
}

function drawAnswerUnits(doc, x, y, units, answerLineHeight) {
  let cursorY = y + ANSWER_PADDING + answerLineHeight * 0.85;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(ANSWER_FONT_SIZE);
  setTextColor(doc);

  units.forEach((unit) => {
    if (unit.type === "gap") {
      cursorY += PARAGRAPH_GAP;
      return;
    }

    doc.text(unit.text, x, cursorY, { lineHeightFactor: ANSWER_LINE_HEIGHT_FACTOR });
    cursorY += answerLineHeight;
  });
}

function collectFormFieldSections(formLabels, formValues) {
  const visitDetailRows = [];
  const contentSections = [];

  if (!Array.isArray(formLabels)) {
    return { visitDetailRows, contentSections };
  }

  const pushNarrative = (title, content) => {
    contentSections.push({ type: "narrative", title, content });
  };

  formLabels.forEach((item) => {
    if (item?.type === "button" || item?.label?.includes("Internal")) return;

    if (item.type === "radio" && Array.isArray(item?.values)) {
      const match = item.values.find((v) => v.value === formValues[item.key]);
      const answer = match ? match.label : "";
      if (answer) pushNarrative(item.label, answer);

      const relatedSection = formLabels.find((f) => f.key === formValues[item.key]);
      if (relatedSection?.components?.length) {
        const nestedRows = relatedSection.components.reduce((rows, component) => {
          let compAnswer = `${formValues[component.key] || ""}`.trim();
          if (component.type === "select" && Array.isArray(component.data?.values)) {
            compAnswer =
              component.data.values
                .filter((opt) => formValues[component.key].includes(opt.value))
                .map((opt) => opt.label)
                .join(",  ") || "";
          }
          if (compAnswer) rows.push({ label: component.label, value: compAnswer });
          return rows;
        }, []);

        if (nestedRows.length) {
          contentSections.push({
            type: "table",
            title: relatedSection.legend || item.label,
            rows: nestedRows,
          });
        }
      }
      return;
    }

    let answer = `${formValues[item.key] || ""}`.trim();
    if (item.type === "select" && Array.isArray(item.data?.values)) {
      const sel = item.data.values.find((v) => v.value === formValues[item.key]);
      answer = sel ? sel.label : "";
    }

    if (!answer) return;

    if (VISIT_DETAIL_LABELS.has(item.label?.toLowerCase()?.trim())) {
      visitDetailRows.push({ label: item.label, value: answer });
      return;
    }

    pushNarrative(item.label, answer);
  });

  return { visitDetailRows, contentSections };
}

export function renderFormReportPdf({
  formName,
  reportInfoRows = [],
  patientVisitRows = [],
  formLabels,
  formValues,
}) {
  const doc = new jsPDF();
  const { visitDetailRows, contentSections } = collectFormFieldSections(formLabels, formValues);
  const allPatientVisitRows = [...patientVisitRows, ...visitDetailRows];

  const ctx = new PdfLayoutContext(doc, MARGIN);
  ctx.y = drawTitle(doc, ctx.y, formName);
  ctx.y = drawDivider(doc, ctx.y);
  ctx.y = drawTableSection(doc, ctx.y, "Report Information", reportInfoRows);
  ctx.y = drawTableSection(doc, ctx.y, "Patient & Visit Details", allPatientVisitRows);

  let isFirstNarrative = true;
  contentSections.forEach((section) => {
    if (section.type === "table") {
      ctx.y = drawTableSection(doc, ctx.y, section.title, section.rows);
      return;
    }

    new QuestionAnswerSection(doc, section.title, section.content).render(ctx, {
      addTopSpacing: isFirstNarrative,
    });
    isFirstNarrative = false;
  });

  return doc;
}

export function buildReportFileName({ medicalRecordNumber, formName, taskDate, getDateString }) {
  const servicedate = getDateString(taskDate);
  const baseName =
    `${medicalRecordNumber ?? formName}_${servicedate ?? ""}`.replace(/^_+|_+$/g, "") ||
    "Report";
  return `${baseName}.pdf`;
}

export function buildTaskReportRows(task, {
  getFullName,
  dateAndTime,
  getDateString,
  getTimeString,
  getTimeSpentFromTimestamps,
}) {
  const reportSubmittedBy = `${getFullName(
    task?.coordinator?.givenName ?? task?.workforce?.givenName,
    task?.coordinator?.familyName ?? task?.workforce?.familyName
  )} (${task?.coordinator?.email ?? task?.workforce?.email}) (${
    task?.coordinator?.jobTitle ?? task?.workforce?.jobTitle
  })`;

  const reportInfoRows = [
    { label: "Submitted By", value: reportSubmittedBy },
    {
      label: "Submitted On",
      value: task?.createdTimestamp ? dateAndTime(task?.createdTimestamp) : "--",
    },
  ];

  if (task?.status) {
    reportInfoRows.push({
      label: "Status",
      value: task.status === "Submitted" ? "Pending Approval" : task.status,
    });
  }

  if (task?.coordinator) {
    reportInfoRows.push({
      label: "On the Volunteer's Behalf",
      value: `${getFullName(
        task?.coordinator.givenName,
        task?.coordinator.familyName
      )} (${task?.coordinator.email})`,
    });
  }

  const patientVisitRows = [];

  if (task?.patient) {
    patientVisitRows.push({
      label: "Patient Name",
      value: `${getFullName(
        task?.patient.givenName,
        task?.patient.familyName
      )} (${task?.patient?.medicalRecordNumber})`,
    });
  }

  if (task?.taskDate) {
    patientVisitRows.push({
      label: "Service Date",
      value: task.taskDate ? getDateString(task.taskDate) : "--",
    });
  }

  if (task?.startTime) {
    patientVisitRows.push({
      label: "Start Time",
      value: task?.startTime ? getTimeString(task.startTime) : "--",
    });
  }

  if (task?.endTime) {
    patientVisitRows.push({
      label: "End Time",
      value: task.endTime ? getTimeString(task.endTime) : "--",
    });
  }

  if (task?.startTime && task?.endTime) {
    patientVisitRows.push({
      label: "Time Spent",
      value: getTimeSpentFromTimestamps(task.startTime, task.endTime) || "0h 0m",
    });
  }

  return { reportInfoRows, patientVisitRows };
}
